import React from 'react'
import { useLogin } from '@refinedev/core'
import { Alert, Button, Card, Form, Input, Typography } from 'antd'
import { EnvSwitch, DemoBanner } from '../components/EnvSwitch'
import { getActiveEnv, DEMO_LOGIN } from '../lib/adminEnv'
import { getActiveTenant } from '../lib/tenants'

type LoginVars = { email: string; password: string }

/**
 * Admin sign-in page. Submits to the Supabase authProvider (signInWithPassword +
 * admin-role verification). Rendered at /login for unauthenticated users; once a
 * session exists the route redirects to the Members list (see App.tsx).
 */
export function Login() {
  const { mutate: login, isPending } = useLogin<LoginVars>()
  const [error, setError] = React.useState<string | null>(null)
  // On the demo backend, prefill the seeded demo admin so staff can sign straight
  // in. Never prefilled against production.
  const demo = !getActiveEnv().isProd
  // Identity for the resolved tenant (its subdomain), so a second tenant never
  // signs in under MUAA's name — spec §11 (crest + label theming).
  const tenant = getActiveTenant()

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* DEMO DATA bar at the very top while on demo — so you never sign in to the
          demo backend without the warning (matches every other page). */}
      <DemoBanner />
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <Card style={{ width: 400, maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            {tenant.label}
          </Typography.Title>
          <Typography.Text type="secondary">Alumni Admin</Typography.Text>
        </div>

        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        ) : null}

        <Form<LoginVars>
          layout="vertical"
          requiredMark={false}
          initialValues={demo ? { email: DEMO_LOGIN.email, password: DEMO_LOGIN.password } : undefined}
          onFinish={(values) => {
            setError(null)
            login(values, {
              onError: (err) =>
                setError(err?.message ?? 'Sign in failed. Check your credentials.'),
            })
          }}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
          >
            <Input size="large" autoComplete="email" placeholder="admin@muaa.test" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Enter your password' }]}
          >
            <Input.Password size="large" autoComplete="current-password" placeholder="••••••••" />
          </Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={isPending}>
            Sign in
          </Button>
        </Form>

        {/* Demo-mode: pick which backend you're signing into. Renders nothing
            unless the demo backend is configured (VITE_DEMO_SUPABASE_*). Lets you
            flip back to production from the login screen too — no dead end. */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid #eceef4',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <EnvSwitch />
        </div>
        </Card>
      </div>
    </div>
  )
}
